import { useState, useEffect, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../redux/typedHooks";
import { setUser } from "../redux/modules/userSlice";
import { getUserPersonalInfo } from "../api/userApi";
import { showToast } from "../utils/toast";

export interface SubscriptionTier {
  id: string;
  tierName: string;
  price: number;
  durationInDays: 30 | 90 | 180;
}

export interface EarningsHistoryItem {
  amount: number;
  type: "subscription" | "ppv" | "custom" | "tip";
  date: Date;
  payoutStatus: boolean;
}

export interface UserPricingData {
  subscriptionPrice: SubscriptionTier[];
  ppvRate: number;
  customContentPrice: number;
  tipMinimum: number;
  earningsHistory: EarningsHistoryItem[];
}

export interface UserPersonalInfo {
  // Basic user info
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  DOB: string | null;
  profileImg: string;
  referralId: number;
  bio?: string;
  userType: {
    _id: string;
    title: string;
    description: string;
  } | null;

  // Verification status
  validated: boolean;
  ageProofed: boolean;
  contentProviderAgreementAccepted: boolean;

  // Pricing data (for creators)
  pricingData?: UserPricingData;

  // Other fields
  uuid: string;
  usernameUpdatedAt: string | null;
  isOnboarding: boolean;
}

interface UseUserPersonalInfoReturn {
  // Data
  userPersonalInfo: UserPersonalInfo | null;
  pricingData: UserPricingData | null;

  // Loading states
  loading: boolean;
  refetching: boolean;

  // Actions
  onRefetch: () => void;
  refetch: () => Promise<void>;
  updateUserInRedux: (updatedData: Partial<UserPersonalInfo>) => void;

  // Error handling
  error: string | null;
}

export const useUserPersonalInfo = (): UseUserPersonalInfoReturn => {
  const dispatch = useAppDispatch();
  const [refetchActive, setRefetchActive] = useState<boolean | number>(false);
  const currentUser = useAppSelector(state => state.user.currentUser);

  // State management
  const [userPersonalInfo, setUserPersonalInfo] =
    useState<UserPersonalInfo | null>(null);
  const [pricingData, setPricingData] = useState<UserPricingData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refetching, setRefetching] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch user personal info
  const fetchUserPersonalInfo = useCallback(
    async (isRefetch = false) => {
      try {
        if (isRefetch) {
          setRefetching(true);
        } else {
          setLoading(true);
        }
        setError(null);

        const response = await getUserPersonalInfo();

        if (response?.success && response?.data) {
          const userData = response.data as UserPersonalInfo;

          // Set local state
          setUserPersonalInfo(userData);

          // Extract pricing data if available
          if (userData.pricingData) {
            setPricingData(userData.pricingData);
          }

          // Update Redux state with fresh data
          // Preserve referralId from current state if it exists and new data doesn't have it
          dispatch(
            setUser({
              ...(currentUser as any),
              ...userData,
              // Preserve referralId if it exists in current state and is not in new data
              referralId:
                userData.referralId || (currentUser as any)?.referralId,
            })
          );

          if (isRefetch) {
            showToast("User information updated successfully!", "success");
          }
        } else {
          throw new Error(
            response?.message || "Failed to fetch user personal info"
          );
        }
      } catch (err) {
        const errorMessage =
          (err as any)?.response?.data?.message ||
          (err as any)?.message ||
          "Failed to fetch user personal info";
        setError(errorMessage);

        if (isRefetch) {
          showToast(errorMessage, "error");
        } else {
          console.error("Error fetching user personal info:", err);
        }
      } finally {
        setLoading(false);
        setRefetching(false);
      }
    },
    [dispatch, currentUser]
  );

  // Refetch function
  const refetch = useCallback(async () => {
    await fetchUserPersonalInfo(true);
  }, [fetchUserPersonalInfo]);

  // Update user in Redux (for optimistic updates)
  const updateUserInRedux = useCallback(
    (updatedData: Partial<UserPersonalInfo>) => {
      if (userPersonalInfo) {
        const updatedUser = {
          ...userPersonalInfo,
          ...updatedData,
        };

        setUserPersonalInfo(updatedUser);
        dispatch(
          setUser({
            ...(currentUser as any),
            ...updatedUser,
          })
        );
      }
    },
    [userPersonalInfo, dispatch, currentUser]
  );

  // Initial fetch
  useEffect(() => {
    fetchUserPersonalInfo(false);
  }, [refetchActive]);

  function onRefetch() {
    setRefetchActive(Date.now());
  }

  return {
    // Data
    userPersonalInfo,
    pricingData,

    // Loading states
    loading,
    refetching,

    // Actions
    refetch,
    updateUserInRedux,

    onRefetch,

    // Error handling
    error,
  };
};

export default useUserPersonalInfo;
